"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { Text } from "@medusajs/ui"

interface PriceRangeSliderProps {
  minPrice: number
  maxPrice: number
  currentMin: number
  currentMax: number
  onRangeChange: (min: number, max: number) => void
  loading?: boolean
  disabled?: boolean
}

export default function PriceRangeSlider({
  minPrice,
  maxPrice,
  currentMin,
  currentMax,
  onRangeChange,
  loading = false,
  disabled = false,
}: PriceRangeSliderProps) {
  const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max)
  // Initialize local range with full range selected (min to max)
  const [localMin, setLocalMin] = useState(() => minPrice)
  const [localMax, setLocalMax] = useState(() => maxPrice)
  const [isDragging, setIsDragging] = useState(false)
  const [dragTarget, setDragTarget] = useState<'min' | 'max' | null>(null)
  const sliderRef = useRef<HTMLDivElement>(null)

  // Update local state when props change
  useEffect(() => {
    // Always start with full range, only update if explicitly set to a different range
    if (currentMin === 0 && currentMax === 10000) {
      // Default values - use full range
      setLocalMin(minPrice)
      setLocalMax(maxPrice)
    } else {
      // Custom range - use the provided values
      setLocalMin(clamp(currentMin, minPrice, maxPrice))
      setLocalMax(clamp(currentMax, minPrice, maxPrice))
    }
  }, [currentMin, currentMax, minPrice, maxPrice])

  const handleMinChange = useCallback((value: number) => {
    const newMin = Math.max(minPrice, Math.min(value, localMax - 1))
    setLocalMin(newMin)
    onRangeChange(newMin, localMax)
  }, [minPrice, localMax, onRangeChange])

  const handleMaxChange = useCallback((value: number) => {
    const newMax = Math.min(maxPrice, Math.max(value, localMin + 1))
    setLocalMax(newMax)
    onRangeChange(localMin, newMax)
  }, [maxPrice, localMin, onRangeChange])

  const handleSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    const numValue = parseInt(value)
    
    if (name === "min") {
      handleMinChange(numValue)
    } else if (name === "max") {
      handleMaxChange(numValue)
    }
  }, [handleMinChange, handleMaxChange])

  const getValueFromPosition = useCallback((clientX: number) => {
    if (!sliderRef.current) return 0
    
    const rect = sliderRef.current.getBoundingClientRect()
    const percent = (clientX - rect.left) / rect.width
    const value = minPrice + (maxPrice - minPrice) * percent
    return clamp(value, minPrice, maxPrice)
  }, [minPrice, maxPrice])

  const handleMouseDown = useCallback((target: 'min' | 'max') => {
    if (disabled || loading) return
    setIsDragging(true)
    setDragTarget(target)
  }, [disabled, loading])

  const handleTouchStart = useCallback((target: 'min' | 'max') => {
    if (disabled || loading) return
    setIsDragging(true)
    setDragTarget(target)
  }, [disabled, loading])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !dragTarget) return
    
    const value = getValueFromPosition(e.clientX)
    
    if (dragTarget === 'min') {
      const newMin = Math.max(minPrice, Math.min(value, localMax - 1))
      setLocalMin(newMin)
      onRangeChange(newMin, localMax)
    } else {
      const newMax = Math.min(maxPrice, Math.max(value, localMin + 1))
      setLocalMax(newMax)
      onRangeChange(localMin, newMax)
    }
  }, [isDragging, dragTarget, getValueFromPosition, minPrice, maxPrice, localMin, localMax, onRangeChange])

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging || !dragTarget) return
    
    e.preventDefault() // Prevent scrolling while dragging
    
    const touch = e.touches[0]
    const value = getValueFromPosition(touch.clientX)
    
    if (dragTarget === 'min') {
      const newMin = Math.max(minPrice, Math.min(value, localMax - 1))
      setLocalMin(newMin)
      onRangeChange(newMin, localMax)
    } else {
      const newMax = Math.min(maxPrice, Math.max(value, localMin + 1))
      setLocalMax(newMax)
      onRangeChange(localMin, newMax)
    }
  }, [isDragging, dragTarget, getValueFromPosition, minPrice, maxPrice, localMin, localMax, onRangeChange])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
    setDragTarget(null)
  }, [])

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false)
    setDragTarget(null)
  }, [])

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('touchmove', handleTouchMove, { passive: false })
      document.addEventListener('touchend', handleTouchEnd)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('touchend', handleTouchEnd)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd])

  const formatPrice = (price: number) => {
    return `€${Math.round(price).toLocaleString()}`
  }

  const getSliderBackground = () => {
    const minPercent = ((localMin - minPrice) / (maxPrice - minPrice)) * 100
    const maxPercent = ((localMax - minPrice) / (maxPrice - minPrice)) * 100
    return `linear-gradient(to right, #e5e7eb 0%, #e5e7eb ${minPercent}%, #3b82f6 ${minPercent}%, #3b82f6 ${maxPercent}%, #e5e7eb ${maxPercent}%, #e5e7eb 100%)`
  }

  return (
    <div className="space-y-4">
      {/* Price Display */}
      <div className="flex justify-between text-sm text-neutral-600">
        <span>{formatPrice(localMin)}</span>
        <span>{formatPrice(localMax)}</span>
      </div>

      {/* Slider Container */}
      <div className="relative" ref={sliderRef}>
        {/* Track */}
        <div 
          className="h-2 bg-neutral-200 rounded-full relative"
          style={{ background: getSliderBackground() }}
        >
          {/* Hidden range inputs for accessibility */}
          <input
            type="range"
            name="min"
            min={minPrice}
            max={maxPrice}
            value={localMin}
            onChange={handleSliderChange}
            disabled={disabled || loading}
            className="sr-only"
          />
          <input
            type="range"
            name="max"
            min={minPrice}
            max={maxPrice}
            value={localMax}
            onChange={handleSliderChange}
            disabled={disabled || loading}
            className="sr-only"
          />
        </div>

        {/* Thumb Indicators */}
        <div className="absolute top-0 left-0 right-0 h-2 pointer-events-none">
          <div 
            className={`absolute w-4 h-4 bg-blue-600 rounded-full border-2 border-white shadow-md -top-1 transform -translate-x-1/2 cursor-pointer hover:scale-110 transition-transform ${
              dragTarget === 'min' ? 'scale-110 shadow-lg' : ''
            }`}
            style={{ 
              left: `${((localMin - minPrice) / (maxPrice - minPrice)) * 100}%`,
              zIndex: 4,
              pointerEvents: 'auto',
              touchAction: 'none'
            }}
            onMouseDown={() => handleMouseDown('min')}
            onTouchStart={() => handleTouchStart('min')}
          />
          <div 
            className={`absolute w-4 h-4 bg-blue-600 rounded-full border-2 border-white shadow-md -top-1 transform -translate-x-1/2 cursor-pointer hover:scale-110 transition-transform ${
              dragTarget === 'max' ? 'scale-110 shadow-lg' : ''
            }`}
            style={{ 
              left: `${((localMax - minPrice) / (maxPrice - minPrice)) * 100}%`,
              zIndex: 4,
              pointerEvents: 'auto',
              touchAction: 'none'
            }}
            onMouseDown={() => handleMouseDown('max')}
            onTouchStart={() => handleTouchStart('max')}
          />
        </div>
      </div>

      {/* Price Range Info */}
      <div className="text-xs text-neutral-500">
        <Text>Razpoložljiv cenovni razpon: {formatPrice(minPrice)} - {formatPrice(maxPrice)}</Text>
      </div>
    </div>
  )
}
